import { AxiosError, AxiosResponse } from 'axios'

import { SerializedBusinessError } from 'shared/api/common/schemas/errors'
import {
  ForbiddenAccessError,
  LegalAccessError,
  UnauthorizedAccessError,
} from 'shared/errors/accessLayerError'
import { CustomError } from 'shared/errors/base'
import { ClientLayerError } from 'shared/errors/clientLayerError'
import { isNetworkError, isWafChallengeError } from 'shared/errors/helpers/guards'
import { InvalidErrorResponseError, ResponseActionError } from 'shared/errors/protocolLayerError'
import {
  NetworkError,
  NotAllowedMethodError,
  RedirectError,
  ServerError,
  TimeoutError,
  WafChallengeError,
} from 'shared/errors/transportLayerError'
import { isLocal } from 'utils/isLocal'
import { replaceIds } from 'utils/replaceIds'
import errorTrackingService from 'utils/services/errorTrackingService'
import {
  CSRF_TOKEN_EMPTY_EVENT_NAME,
  CSRF_TOKEN_EMPTY_FINGERPRINT,
} from 'utils/services/sentryConstantNames'

import { accountInstance, baseInstance } from './index'
import {
  ClientStatusesRange,
  checkValidStatusRange,
  convertResponseWithoutState,
  RedirectStatusesRange,
  ServerStatusesRange,
  shouldLogWafChallengeLoop,
  shouldReloadOnWafChallenge,
} from './lib/interceptors'

type InitApi = (options: {
  getCurrentAccountId: () => string
  getCsrfToken: () => string | null
}) => void

export const initApi: InitApi = ({ getCurrentAccountId, getCsrfToken }) => {
  const instances = [baseInstance, accountInstance]

  instances.forEach((instance) => {
    instance.interceptors.request.use((request) => {
      if (request.baseURL && request.baseURL.includes(':currentAccountID')) {
        request.baseURL = request.baseURL.replace(':currentAccountID', getCurrentAccountId())
      }

      const csrfToken = getCsrfToken()
      if (csrfToken) {
        request.headers.set('X-Csrf-Token', csrfToken)
      } else {
        errorTrackingService.logWarning(new ClientLayerError(CSRF_TOKEN_EMPTY_EVENT_NAME), {
          scope: 'root',
          section: null,
          extra: { url: `${request.baseURL ?? ''}${request.url ?? ''}` },
          fingerprint: CSRF_TOKEN_EMPTY_FINGERPRINT,
        })
      }

      request.headers.set(
        'X-Frontend-Bundle',
        window.STATIC_VERSION ? String(window.STATIC_VERSION) : 'Empty',
      )

      return request
    })

    instance.interceptors.response.use(
      (response) => {
        const responseWithoutState = convertResponseWithoutState(response)

        if (response.data.state === false) {
          if (Array.isArray(response.data.$errors)) {
            return Promise.reject(new ResponseActionError(responseWithoutState))
          } else {
            return Promise.reject(new InvalidErrorResponseError(response))
          }
        }

        return responseWithoutState
      },
      (error: AxiosError) => {
        const status = error.response?.status
        const parsedUrl = replaceIds(error.config?.url ?? error.request?.url ?? 'undefined')

        errorTrackingService.setTag('status', status)
        let mappedError = new CustomError('UnknownError', {
          cause: error,
        })

        if (error.code === AxiosError.ETIMEDOUT || error.code === AxiosError.ECONNABORTED) {
          mappedError = new TimeoutError()
        }

        if (error.code === AxiosError.ERR_NETWORK) {
          mappedError = new NetworkError()
        }

        if (checkValidStatusRange(ServerStatusesRange, status)) {
          mappedError = new ServerError(error.message)
        }

        if (checkValidStatusRange(ClientStatusesRange, status)) {
          switch (status) {
            case 401:
              !isLocal() && window.location.assign('/login')
              mappedError = new UnauthorizedAccessError(error.message)
              break
            case 403:
              mappedError = new ForbiddenAccessError(error.message, parsedUrl)
              break
            case 405: {
              const wafAction = error.response?.headers['x-amzn-waf-action']
              mappedError =
                typeof wafAction === 'string' && wafAction
                  ? new WafChallengeError(error.config?.method || 'UNKNOWN', parsedUrl, wafAction)
                  : new NotAllowedMethodError(error.config?.method || 'UNKNOWN', parsedUrl)
              break
            }
            case 429: {
              const data = error.response?.data as
                | { state?: boolean; $errors?: SerializedBusinessError[] }
                | undefined
              if (data && data.state === false && Array.isArray(data.$errors)) {
                return Promise.reject(
                  new ResponseActionError(
                    error.response as AxiosResponse<{ $errors: SerializedBusinessError[] }>,
                  ),
                )
              }
              break
            }
            case 451:
              mappedError = new LegalAccessError(error.message)
              break
            default:
              break
          }
        }

        if (checkValidStatusRange(RedirectStatusesRange, status)) {
          mappedError = new RedirectError(parsedUrl || 'UNKNOWN')
        }

        const { scope, section } = mappedError?.getLogOptions() ?? {}

        const isWafChallenge = isWafChallengeError(mappedError)
        const isNetwork = isNetworkError(mappedError)
        const isWafReload = isWafChallenge && !isLocal()

        // Reload as a top-level GET so AWS WAF serves the challenge natively (AJAX can't render it).
        if (isWafReload && shouldReloadOnWafChallenge()) {
          mappedError.onReported()
          window.location.reload()
          // Never settle so no caller's .catch/handleError shows an error toast mid-reload.
          return new Promise<never>(() => {})
        }

        const isRepeatedWafChallengeLoop = isWafReload && !shouldLogWafChallengeLoop()

        if (isNetwork || isRepeatedWafChallengeLoop) {
          // Mark as "reported" so it won't be reported again where it's thrown/caught
          mappedError.onReported()
        } else {
          errorTrackingService.logWarning(mappedError, {
            tags: {
              status: status || 'undefined',
              transaction: replaceIds(error.request.url || 'undefined'),
            },
            // The error's own class fingerprint is prepended by logWarning.
            // Too many reload attempts already - a distinct fingerprint surfaces the loop.
            fingerprint: isWafReload ? [parsedUrl, 'waf-challenge-reload-loop'] : [parsedUrl],
            scope: scope || 'root',
            section: section || null,
          })
        }

        return Promise.reject(mappedError)
      },
    )
  })
}
